import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import styles from "../SearchSharePreview.module.scss";
import { RawChartItem } from "src/components/charts/LineChart/types";
import { TableChartColumn } from "src/components/charts/TableChart/types";
import { LineChart, PieChartWithLabels, TableChart } from "src/components";
import {
  selectWidgetData,
  selectTrackersById,
  selectTrackersCollectionById,
} from "src/store/selectors";
import {
  formatToLocaleNumber,
  calculateValueChangeStyle,
  formatToMonthFullYearDate,
} from "src/utils";

// Inner imports
import { WIDGET_ID } from "../constants";
import { SearchShareTableItem } from "../types";
import {
  renderNameColumn,
  renderPieChartLabel,
  renderPieChartValue,
  renderTrendChangeColumn,
  formatSearchSharePieChart,
  formatSearchShareLineChart,
  renderPieChartTooltipLabel,
  formatSearchShareTableChart,
  calculatePieChartTotalValue,
} from "../utils";

export const useSearchShareCharts = ({
  trackerIds,
  isExpanded,
  dashboardDateRangeId,
  trackersCollectionId,
}: Widget.ChartProps): Widget.Chart[] => {
  const { t } = useTranslation();

  const widgetData = useSelector((state: Store.RootState) =>
    selectWidgetData(state, dashboardDateRangeId, WIDGET_ID),
  );

  const trackersCollection = useSelector((state: Store.RootState) =>
    selectTrackersCollectionById(state, trackersCollectionId),
  );

  const trackers = useSelector((state: Store.RootState) =>
    selectTrackersById(state, trackerIds),
  );

  const trackersCollectionTrackerIds = useMemo<Tracker.Data["id"][]>(
    () => trackersCollection?.trackerIds || [],
    [trackersCollection?.trackerIds],
  );

  const formattedTrackers = useMemo<Pick<Tracker.Data, "id" | "name" | "keywordsDataSources">[]>(
    () =>
      trackers
        .map(({ id, name, keywordsDataSources }) => ({ id, name, keywordsDataSources }))
        .sort((a, b) => trackerIds.indexOf(a.id) - trackerIds.indexOf(b.id)),
    [trackerIds, trackers],
  );

  const { lineChart, pieChart, tableChart, startDate, endDate } =
    useMemo(() => {
      const { data, startDate = "", endDate = "" } = widgetData || {};

      const { lineChart = {}, pieChart = {}, tableChart = {} } = data || {};

      return { lineChart, pieChart, tableChart, startDate, endDate };
    }, [widgetData]);

  const formattedLineChart = useMemo<RawChartItem[]>(() => {
    if (!Object.values(lineChart).length) return [];

    return formatSearchShareLineChart({ lienChartData: lineChart, trackerIds });
  }, [lineChart, trackerIds]);

  const formattedPieChart = useMemo<Widgets.PieChart.Item>(() => {
    if (!Object.values(pieChart).length)
      return { startDate, endDate, values: [] };

    return formatSearchSharePieChart({
      endDate,
      startDate,
      trackerIds,
      pieChartData: pieChart,
      lineChartData: formattedLineChart,
    });
  }, [pieChart, formattedLineChart, startDate, endDate, trackerIds]);

  const formattedTableChart = useMemo(() => {
    if (!Object.values(tableChart).length) return [];

    return formatSearchShareTableChart({
      trackers,
      pieChartData: formattedPieChart,
    });
  }, [formattedPieChart, tableChart, trackers]);

  const tableColumns = useMemo<TableChartColumn<SearchShareTableItem>[]>(
    () => [
      {
        key: "name",
        width: "50%",
        style: { fontWeight: 500, padding: "15px 10px" },
        label: t("widget.search_share.table.label.name"),
        valueFormatter: ({ id, name }) =>
          renderNameColumn({
            trackerId: id,
            trackerName: name,
            trackersCollectionTrackerIds,
          }),
      },
      {
        key: "percentage",
        label: t("widget.search_share.table.label.total_search"),
        valueFormatter: ({ percentage }) => `${percentage}%`,
      },
      {
        key: "trendChange",
        label: t("widget.search_share.table.label.trend_change"),
        style: (value) => calculateValueChangeStyle(value?.trendChange),
        valueFormatter: ({ trendChange }) =>
          renderTrendChangeColumn(trendChange),
      },
    ],
    [t, trackersCollectionTrackerIds],
  );

  return useMemo<Widget.Chart[]>(() => {
    const charts: Widget.Chart[] = [];

    const [hasLineChart, hasPieChart, hasTableChart] = [
      Boolean(formattedLineChart.length),
      Boolean(formattedPieChart.values.length),
      Boolean(formattedTableChart.length),
    ];

    if (hasLineChart)
      charts.push({
        type: "lineChart",
        chart: (
          <LineChart
            className={styles.chart}
            data={formattedLineChart}
            trackers={formattedTrackers}
            trackersCollectionId={trackersCollectionId}
            allTrackerIds={trackersCollectionTrackerIds}
            chartSettings={{
              hasTrendLine: false,
              isTooltipOverflowAvailable: !isExpanded,
            }}
            axis={{
              yAxisVerticalPadding: { top: 10, bottom: 0 },
              yAxisValueFormatter: (value) => `${value}%`,
              yAxisAdditionalValueFormatter: formatToLocaleNumber,
            }}
          />
        ),
      });

    if (hasPieChart)
      charts.push({
        type: "pieChart",
        chart: (
          <>
            <PieChartWithLabels
              hasLabels={!isExpanded}
              data={formattedPieChart}
              trackers={formattedTrackers}
              renderValue={renderPieChartValue}
              renderLabel={renderPieChartLabel}
              className={styles.pieChartWrapper}
              dateFormatter={formatToMonthFullYearDate}
              allTrackerIds={trackersCollectionTrackerIds}
              valueFormatter={(value) => `${Math.round(value)}%`}
              renderTooltipLabel={renderPieChartTooltipLabel}
              calculateTotalValue={calculatePieChartTotalValue}
            />
            {isExpanded && hasTableChart && (
              <TableChart
                hasNumeration={false}
                columns={tableColumns}
                data={formattedTableChart}
                className={styles.tableChart}
                hasResponsivePagination={false}
                defaultSort={{ percentage: "DESC" }}
              />
            )}
          </>
        ),
      });

    return charts;
  }, [
    isExpanded,
    tableColumns,
    formattedPieChart,
    formattedTrackers,
    formattedLineChart,
    formattedTableChart,
    trackersCollectionId,
    trackersCollectionTrackerIds,
  ]);
};
